import React, { useState, useEffect } from 'react';
import { FieldSchema, FieldType, SignatureMethod, FormMetadata, MatrixColumn, Sector } from '../../types';
import { db } from '@/src/services/db';
import { useAuth } from '@/src/context/AuthContext';
import { 
  Plus, Trash2, GripVertical, Settings, Type, Hash, 
  Calendar, Camera, PenTool, List, ArrowLeft, Save, 
  AlignLeft, CheckSquare, Smartphone, History, CornerUpLeft, Film,
  Star, Radio, ListChecks, MoveHorizontal, ToggleLeft, Table
} from 'lucide-react';

interface FormBuilderProps {
  onBack: () => void;
  existingForm?: FormMetadata;
}

// Trava de Esquema (Schema Freeze)
const MATERIALIZED_FORMS = ['form-1775762620276', 'form-1775766905782'];

const FormBuilder: React.FC<FormBuilderProps> = ({ onBack, existingForm }) => {
  const { user } = useAuth();
  const isMaterialized = existingForm ? MATERIALIZED_FORMS.includes(existingForm.id) : false;
  const [title, setTitle] = useState(existingForm?.title || 'Novo Formulário de Inspeção');
  const [description, setDescription] = useState(existingForm?.description || '');
  const [requireLocation, setRequireLocation] = useState(existingForm?.requireLocation || false);
  const [permitirEdicao, setPermitirEdicao] = useState(existingForm?.permitir_edicao || false);
  const [fields, setFields] = useState<FieldSchema[]>(
    existingForm?.versions[existingForm.versions.length - 1].schema || []
  );
  
  // UX State
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  // Sector Management (New)
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [sectorId, setSectorId] = useState(existingForm?.sectorId || '');

  // Load Sectors
  useEffect(() => {
    db.getSectors().then(setSectors);
  }, []);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handlePublish();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fields, title, description, sectorId, requireLocation, permitirEdicao]);

  const addField = (type: FieldType) => {
    const newField: FieldSchema = {
      id: `f-${Date.now()}`,
      type,
      label: 'Nova Pergunta',
      required: false,
    };
    
    // Default configs
    if (type === FieldType.SIGNATURE) {
        newField.signatureConfig = { method: SignatureMethod.HANDWRITTEN, allowRefusal: true };
    }
    if ([FieldType.SELECT, FieldType.RADIO, FieldType.CHECKBOX].includes(type)) {
        newField.options = [{ label: 'Opção 1', value: 'opt1' }];
    }
    if (type === FieldType.MEDIA_PHOTO) {
        newField.mediaConfig = { allowMultiple: false, allowVideo: false };
    }
    if (type === FieldType.RATING) {
        newField.ratingConfig = { maxStars: 5 };
    }
    if (type === FieldType.LINEAR_SCALE) {
        newField.linearScaleConfig = { min: 1, max: 5, minLabel: 'Ruim', maxLabel: 'Excelente' };
    }
    if (type === FieldType.MATRIX) {
        newField.matrixConfig = { 
            columns: [
                { id: 'c1', label: 'Item', type: 'text' },
                { id: 'c2', label: 'Quantidade', type: 'number' }
            ] 
        };
    }

    setFields([...fields, newField]);
    setSelectedFieldId(newField.id); // Auto-select new field
  };

  const removeField = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    const newFields = [...fields];
    const removedId = newFields[index].id;
    newFields.splice(index, 1);
    setFields(newFields);
    if (selectedFieldId === removedId) setSelectedFieldId(null);
  };

  const updateField = (id: string, key: keyof FieldSchema, value: any) => {
    setFields(fields.map(f => f.id === id ? { ...f, [key]: value } : f));
  };

  const updateNestedConfig = (id: string, configKey: 'signatureConfig' | 'mediaConfig' | 'ratingConfig' | 'linearScaleConfig' | 'matrixConfig', key: string, value: any) => {
    setFields(fields.map(f => {
        if (f.id !== id) return f;
        const currentConfig = f[configKey] || {};
        return { ...f, [configKey]: { ...currentConfig, [key]: value } };
    }));
  };

  const updateMatrixColumns = (id: string, columns: MatrixColumn[]) => {
      setFields(fields.map(f => {
          if (f.id !== id) return f;
          return { ...f, matrixConfig: { ...f.matrixConfig, columns } };
      }));
  };

  const handlePublish = async () => {
    if (!title) return alert("O título é obrigatório");
    if (!sectorId) return alert("Selecione um Setor para classificar este formulário.");
    if (fields.length === 0) return alert("Adicione pelo menos um campo");
    if (!user) return alert("Sessão expirada");

    if (existingForm && !window.confirm(`A publicação criará a Versão ${existingForm.currentVersion + 1}. Continuar?`)) {
        return;
    }

    const selectedSector = sectors.find(s => s.id === sectorId);

    try {
      await db.saveForm(
        {
          id: existingForm?.id,
          title,
          description,
          sectorId: sectorId,
          sector: selectedSector?.name || 'Geral', // Legacy fallback
          requireLocation,
          permitir_edicao: permitirEdicao
        },
        fields,
        user
      );
      alert("Formulário publicado com sucesso!");
      onBack();
    } catch (error: any) {
      console.error("Erro ao publicar formulário:", error);
      alert(`Erro ao publicar: ${error.message || 'Erro desconhecido'}`);
    }
  };

  const selectedField = fields.find(f => f.id === selectedFieldId);

  // --- Render Helpers ---

  const getIconForType = (type: FieldType) => {
    switch (type) {
        case FieldType.TEXT_SHORT: return <Type size={16}/>;
        case FieldType.TEXT_LONG: return <AlignLeft size={16}/>;
        case FieldType.NUMBER: return <Hash size={16}/>;
        case FieldType.DATE: return <Calendar size={16}/>;
        case FieldType.SELECT: return <List size={16}/>;
        case FieldType.RADIO: return <Radio size={16}/>;
        case FieldType.CHECKBOX: return <ListChecks size={16}/>;
        case FieldType.YES_NO: return <ToggleLeft size={16}/>;
        case FieldType.RATING: return <Star size={16}/>;
        case FieldType.LINEAR_SCALE: return <MoveHorizontal size={16}/>;
        case FieldType.MATRIX: return <Table size={16}/>;
        case FieldType.MEDIA_PHOTO: return <Camera size={16}/>;
        case FieldType.SIGNATURE: return <PenTool size={16}/>;
        default: return <Type size={16}/>;
    }
  };

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">
      
      {/* COLUMN 1: TOOLBOX (Left) */}
      <div className="w-64 bg-white border-r border-slate-200 flex flex-col z-10 shadow-sm flex-shrink-0">
        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded text-slate-500">
                <ArrowLeft size={20} />
            </button>
            <span className="font-bold text-slate-700">Ferramentas</span>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Campos de Entrada</p>
            
            <button onClick={() => addField(FieldType.TEXT_SHORT)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <Type size={18}/> Texto Curto
            </button>
            <button onClick={() => addField(FieldType.TEXT_LONG)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <AlignLeft size={18}/> Texto Longo
            </button>
            <button onClick={() => addField(FieldType.NUMBER)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <Hash size={18}/> Número
            </button>
            <button onClick={() => addField(FieldType.DATE)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <Calendar size={18}/> Seletor de Data
            </button>
            
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 mt-4">Seleção & Avaliação</p>
            
            <button onClick={() => addField(FieldType.YES_NO)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <ToggleLeft size={18}/> Sim / Não
            </button>
            <button onClick={() => addField(FieldType.SELECT)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <List size={18}/> Lista Suspensa
            </button>
            <button onClick={() => addField(FieldType.RADIO)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <Radio size={18}/> Múltipla Escolha
            </button>
             <button onClick={() => addField(FieldType.CHECKBOX)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <ListChecks size={18}/> Caixa de Seleção
            </button>
            <button onClick={() => addField(FieldType.RATING)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <Star size={18}/> Classificação
            </button>
            <button onClick={() => addField(FieldType.LINEAR_SCALE)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <MoveHorizontal size={18}/> Escala Linear
            </button>

            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 mt-4">Dados Ricos</p>

            <button onClick={() => addField(FieldType.MATRIX)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <Table size={18}/> Matriz de Dados
            </button>
            <button onClick={() => addField(FieldType.MEDIA_PHOTO)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <Camera size={18}/> Foto Evidência
            </button>
            <button onClick={() => addField(FieldType.SIGNATURE)} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded text-slate-600 hover:text-indigo-600 transition-all text-sm font-medium">
                <PenTool size={18}/> Assinatura
            </button>
        </div>
      </div>

      {/* COLUMN 2: CANVAS (Center - Mobile Preview) */}
      <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-100 relative">
        <div className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shadow-sm z-10 flex-shrink-0">
            <div className="flex items-center gap-4">
                <div>
                    <span className="text-xs text-slate-400 uppercase font-bold">Rascunho da Versão {existingForm ? existingForm.currentVersion + 1 : '1.0'}</span>
                    <h1 className="font-bold text-slate-800 truncate max-w-xs md:max-w-md">{title}</h1>
                </div>
                {/* Version History Tab Placeholder */}
                <button className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded hover:bg-slate-200">
                    <History size={14}/> Versões
                </button>
            </div>
            
            <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 hidden lg:inline mr-2">Ctrl+S para salvar</span>
                <button onClick={handlePublish} className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded shadow hover:bg-indigo-700 font-medium">
                    <Save size={18}/> Publicar
                </button>
            </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 flex justify-center bg-slate-200/50" onClick={() => setSelectedFieldId(null)}>
            
            {/* PHONE FRAME */}
            <div className="w-[375px] min-h-[700px] h-fit bg-white border-[8px] border-slate-800 rounded-[30px] shadow-2xl relative overflow-hidden flex flex-col mb-20">
                {/* Phone Notch/Status Bar Simulation */}
                <div className="bg-slate-800 h-6 w-full flex justify-center">
                    <div className="h-4 w-32 bg-black rounded-b-xl"></div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* App Header Simulation */}
                    <div className="border-b pb-2 mb-4">
                        <h2 className="font-bold text-slate-800">{title || 'Título do Formulário'}</h2>
                        <p className="text-xs text-slate-400">
                            Setor: {sectors.find(s => s.id === sectorId)?.name || 'Não Selecionado'}
                        </p>
                    </div>

                    {fields.length === 0 && (
                        <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-400 text-sm">
                            Formulário vazio.<br/>Adicione campos da caixa de ferramentas.
                        </div>
                    )}

                    {fields.map((field, idx) => (
                        <div 
                            key={field.id}
                            onClick={(e) => { e.stopPropagation(); setSelectedFieldId(field.id); }}
                            className={`
                                relative group p-3 rounded-lg border-2 transition-all cursor-pointer bg-white text-sm
                                ${selectedFieldId === field.id ? 'border-indigo-500 shadow-lg ring-1 ring-indigo-500' : 'border-transparent hover:border-slate-300 shadow-sm'}
                            `}
                        >
                            <div className="flex items-start gap-2">
                                <div className="mt-1 text-slate-300 group-hover:text-slate-500 cursor-move">
                                    <GripVertical size={16}/>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="text-indigo-500 bg-indigo-50 p-1 rounded">
                                            {getIconForType(field.type)}
                                        </div>
                                        <span className="font-medium text-slate-900">{field.label}</span>
                                        {field.required && <span className="text-red-500 text-xs">*</span>}
                                    </div>
                                    <div className="pl-8 opacity-60 pointer-events-none">
                                        {/* Mock Visual Representation */}
                                        {field.type === FieldType.TEXT_SHORT && <div className="h-8 bg-slate-100 rounded border border-slate-200 w-full"></div>}
                                        {field.type === FieldType.TEXT_LONG && <div className="h-12 bg-slate-100 rounded border border-slate-200 w-full"></div>}
                                        
                                        {field.type === FieldType.RATING && (
                                            <div className="flex gap-1 text-slate-300">
                                                {[...Array(field.ratingConfig?.maxStars || 5)].map((_, i) => <Star key={i} size={16} fill="currentColor"/>)}
                                            </div>
                                        )}

                                        {field.type === FieldType.YES_NO && (
                                            <div className="flex gap-2">
                                                <div className="px-3 py-1 bg-slate-100 border rounded text-slate-500">Sim</div>
                                                <div className="px-3 py-1 bg-slate-100 border rounded text-slate-500">Não</div>
                                            </div>
                                        )}

                                        {field.type === FieldType.RADIO && (
                                            <div className="space-y-1 mt-2">
                                                {field.options?.map((opt, i) => (
                                                    <div key={i} className="flex items-center gap-2">
                                                        <div className="w-3 h-3 rounded-full border border-slate-400"></div> 
                                                        <span className="text-slate-600">{opt.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {field.type === FieldType.CHECKBOX && (
                                            <div className="space-y-1 mt-2">
                                                {field.options?.map((opt, i) => (
                                                    <div key={i} className="flex items-center gap-2">
                                                        <div className="w-3 h-3 rounded border border-slate-400"></div> 
                                                        <span className="text-slate-600">{opt.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {field.type === FieldType.SELECT && (
                                            <div className="h-8 bg-slate-100 rounded border border-slate-200 w-full flex items-center justify-between px-2 text-slate-400">
                                                <span>Selecione...</span>
                                                <List size={12}/>
                                            </div>
                                        )}

                                        {field.type === FieldType.LINEAR_SCALE && (
                                            <div className="flex justify-between items-center text-[10px] text-slate-400 px-2">
                                                <span>{field.linearScaleConfig?.min || 1}</span>
                                                <div className="flex gap-1">
                                                    {[...Array(5)].map((_,i) => <div key={i} className="w-2 h-2 rounded-full border border-slate-300"></div>)}
                                                </div>
                                                <span>{field.linearScaleConfig?.max || 5}</span>
                                            </div>
                                        )}

                                        {field.type === FieldType.MATRIX && (
                                            <div className="border rounded bg-slate-50 text-[10px]">
                                                <div className="flex border-b bg-slate-100 p-1">
                                                    {field.matrixConfig?.columns.map(c => (
                                                        <div key={c.id} className="flex-1 font-bold text-slate-600">{c.label}</div>
                                                    ))}
                                                </div>
                                                <div className="p-1 flex text-slate-300">
                                                    {field.matrixConfig?.columns.map(c => (
                                                        <div key={c.id} className="flex-1 bg-white border h-4 mx-1"></div>
                                                    ))}
                                                </div>
                                                <div className="p-1 text-center text-indigo-400 font-bold">+ Adicionar Linha</div>
                                            </div>
                                        )}

                                        {field.type === FieldType.SIGNATURE && (
                                            <div className="h-16 bg-slate-50 border-2 border-dashed border-slate-200 rounded flex items-center justify-center text-slate-400 text-[10px]">
                                                Área de Assinatura
                                            </div>
                                        )}
                                        {field.type === FieldType.MEDIA_PHOTO && (
                                            <div className="h-16 bg-slate-50 border border-slate-200 rounded flex flex-col items-center justify-center text-slate-400 text-[10px]">
                                                <Camera size={14} className="mb-1"/> Tirar Foto
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button 
                                    onClick={(e) => removeField(e, idx)}
                                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 p-1 transition-opacity"
                                >
                                    <Trash2 size={16}/>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                
                {/* Phone Bottom Bar */}
                <div className="bg-slate-50 h-12 border-t flex items-center justify-center">
                    <div className="w-1/3 h-1 bg-slate-300 rounded-full"></div>
                </div>
            </div>

        </div>
      </div>

      {/* COLUMN 3: PROPERTIES (Right) */}
      <div className="w-80 bg-slate-50 border-l border-slate-200 flex flex-col z-10 shadow-sm overflow-y-auto flex-shrink-0">
        <div className="p-4 border-b border-slate-200 bg-white">
            <span className="font-bold text-slate-800 flex items-center gap-2">
                <Settings size={18}/> Propriedades
            </span>
        </div>

        <div className="p-6 space-y-6">
            {!selectedField ? (
                // GLOBAL FORM SETTINGS
                <>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-700 uppercase">Título do Formulário</label>
                        <input 
                            value={title} 
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-700 uppercase">Setor</label>
                        <select
                            value={sectorId}
                            onChange={(e) => setSectorId(e.target.value)}
                            className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        >
                            <option value="">Selecione...</option>
                            {sectors.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                        <p className="text-[10px] text-slate-500">Defina quem pode ver este formulário.</p>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-700 uppercase">Descrição</label>
                        <textarea
              value={description}
              disabled={isMaterialized}
              onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white border border-slate-300 rounded">
                        <div>
                            <span className="text-sm font-bold text-slate-700">Exigir Localização</span>
                            <p className="text-[10px] text-slate-500">Bloqueia o preenchimento até o usuário autorizar o GPS.</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setRequireLocation(!requireLocation)}
                            className={`w-10 h-6 rounded-full flex items-center p-1 transition-colors ${requireLocation ? 'bg-indigo-600' : 'bg-slate-300'}`}
                        >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${requireLocation ? 'translate-x-4' : 'translate-x-0'}`}></div>
                        </button>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white border border-slate-300 rounded">
                        <div>
                            <span className="text-sm font-bold text-slate-700">Permitir Edição pelo Admin</span>
                            <p className="text-[10px] text-slate-500">Permite editar dados da resposta após envio. O hash será invalidado.</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setPermitirEdicao(!permitirEdicao)}
                            className={`w-10 h-6 rounded-full flex items-center p-1 transition-colors ${permitirEdicao ? 'bg-indigo-600' : 'bg-slate-300'}`}
                        >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${permitirEdicao ? 'translate-x-4' : 'translate-x-0'}`}></div>
                        </button>
                    </div>
                    <div className="p-4 bg-blue-50 text-blue-800 text-xs rounded leading-relaxed border border-blue-200 shadow-sm">
                        <strong>Árvore Lógica:</strong> Selecione um campo para configurar regras de visibilidade ou validação específica.
                    </div>
                </>
            ) : (
                // FIELD SPECIFIC SETTINGS
                <>
                    <div className="flex justify-between items-center pb-2 border-b border-slate-200 mb-2">
                        <span className="text-xs font-mono text-slate-500">{selectedField.id}</span>
                        <button onClick={() => setSelectedFieldId(null)} className="text-xs text-indigo-700 flex items-center gap-1 hover:underline font-semibold">
                            <CornerUpLeft size={10}/> Voltar ao Global
                        </button>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-700 uppercase">Rótulo do Campo</label>
                        <input 
                            value={selectedField.label} 
                            onChange={(e) => updateField(selectedField.id, 'label', e.target.value)}
                            className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none shadow-sm"
                        />
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                        <input 
                            type="checkbox" 
                            id="req-check"
                            checked={selectedField.required}
                            onChange={(e) => updateField(selectedField.id, 'required', e.target.checked)}
                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-slate-300"
                        />
                        <label htmlFor="req-check" className="text-sm font-bold text-slate-700">Campo Obrigatório</label>
                    </div>

                    {selectedField.type === FieldType.MATRIX && (
                         <div className="mt-6 border-t border-slate-200 pt-4 space-y-4">
                            <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2"><Table size={14}/> Colunas da Matriz</h4>
                            <div className="space-y-3">
                                {selectedField.matrixConfig?.columns.map((col, idx) => (
                                    <div key={col.id} className="bg-white p-3 rounded border border-slate-200 shadow-sm space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs font-bold text-slate-500 uppercase">Coluna {idx + 1}</span>
                                            <button 
                                                onClick={() => {
                                                    const newCols = [...(selectedField.matrixConfig?.columns || [])];
                                                    newCols.splice(idx, 1);
                                                    updateMatrixColumns(selectedField.id, newCols);
                                                }}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <Trash2 size={14}/>
                                            </button>
                                        </div>
                                        <input 
                                            placeholder="Nome da Coluna"
                                            value={col.label}
                                            onChange={(e) => {
                                                const newCols = [...(selectedField.matrixConfig?.columns || [])];
                                                newCols[idx].label = e.target.value;
                                                updateMatrixColumns(selectedField.id, newCols);
                                            }}
                                            className="w-full border border-slate-300 rounded p-1.5 text-sm text-slate-900"
                                        />
                                        <select
                                            value={col.type}
                                            onChange={(e) => {
                                                const newCols = [...(selectedField.matrixConfig?.columns || [])];
                                                newCols[idx].type = e.target.value as any;
                                                updateMatrixColumns(selectedField.id, newCols);
                                            }}
                                            className="w-full border border-slate-300 rounded p-1.5 text-sm text-slate-900 bg-white"
                                        >
                                            <option value="text">Texto</option>
                                            <option value="number">Número</option>
                                            <option value="select">Lista (Enum)</option>
                                        </select>
                                        {col.type === 'select' && (
                                            <input 
                                                placeholder="Opções (sep. por vírgula)"
                                                value={col.options?.join(', ') || ''}
                                                onChange={(e) => {
                                                    const newCols = [...(selectedField.matrixConfig?.columns || [])];
                                                    newCols[idx].options = e.target.value.split(',').map(s => s.trim());
                                                    updateMatrixColumns(selectedField.id, newCols);
                                                }}
                                                className="w-full border border-slate-300 rounded p-1.5 text-xs text-slate-900"
                                            />
                                        )}
                                    </div>
                                ))}
                                <button 
                                    onClick={() => {
                                        const newCols = [...(selectedField.matrixConfig?.columns || []), { id: `c${Date.now()}`, label: 'Nova Coluna', type: 'text' as const }];
                                        updateMatrixColumns(selectedField.id, newCols);
                                    }}
                                    className="w-full py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-sm font-bold hover:bg-indigo-100"
                                >
                                    + Adicionar Coluna
                                </button>
                            </div>

                            {/* Prevent Duplicates Toggle — only when 1st column is select */}
                            {selectedField.matrixConfig?.columns[0]?.type === 'select' && (
                                <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded mt-3">
                                    <div>
                                        <span className="text-sm font-bold text-slate-700">Impedir seleção repetida na 1ª coluna</span>
                                        <p className="text-[10px] text-slate-500 mt-0.5">Remove das opções os itens já selecionados em outras linhas. Não ative se a inspeção exigir múltiplas linhas do mesmo item.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => updateNestedConfig(selectedField.id, 'matrixConfig', 'preventDuplicatesOnFirstColumn', !selectedField.matrixConfig?.preventDuplicatesOnFirstColumn)}
                                        className={`w-10 h-6 rounded-full flex items-center p-1 transition-colors flex-shrink-0 ml-3 ${selectedField.matrixConfig?.preventDuplicatesOnFirstColumn ? 'bg-indigo-600' : 'bg-slate-300'}`}
                                    >
                                        <div className={`w-4 h-4 bg-white rounded-full transition-transform ${selectedField.matrixConfig?.preventDuplicatesOnFirstColumn ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {selectedField.type === FieldType.RATING && (
                        <div className="mt-6 border-t border-slate-200 pt-4 space-y-4">
                            <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2"><Star size={14}/> Configuração de Estrelas</h4>
                            <div>
                                <label className="text-xs font-bold text-slate-700">Quantidade Máxima</label>
                                <input 
                                    type="number"
                                    min="3" max="10"
                                    value={selectedField.ratingConfig?.maxStars || 5}
                                    onChange={(e) => updateNestedConfig(selectedField.id, 'ratingConfig', 'maxStars', parseInt(e.target.value))}
                                    className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900"
                                />
                            </div>
                        </div>
                    )}

                    {selectedField.type === FieldType.LINEAR_SCALE && (
                        <div className="mt-6 border-t border-slate-200 pt-4 space-y-4">
                             <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2"><MoveHorizontal size={14}/> Configuração de Escala</h4>
                             <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs font-bold text-slate-700">Mínimo</label>
                                    <select 
                                        value={selectedField.linearScaleConfig?.min || 1}
                                        onChange={(e) => updateNestedConfig(selectedField.id, 'linearScaleConfig', 'min', parseInt(e.target.value))}
                                        className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900"
                                    >
                                        <option value="0">0</option>
                                        <option value="1">1</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-700">Máximo</label>
                                    <select 
                                        value={selectedField.linearScaleConfig?.max || 5}
                                        onChange={(e) => updateNestedConfig(selectedField.id, 'linearScaleConfig', 'max', parseInt(e.target.value))}
                                        className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900"
                                    >
                                        {[3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                             </div>
                             <div>
                                <label className="text-xs font-bold text-slate-700">Rótulo Mínimo</label>
                                <input 
                                    value={selectedField.linearScaleConfig?.minLabel || ''}
                                    placeholder="ex: Ruim"
                                    onChange={(e) => updateNestedConfig(selectedField.id, 'linearScaleConfig', 'minLabel', e.target.value)}
                                    className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900"
                                />
                             </div>
                             <div>
                                <label className="text-xs font-bold text-slate-700">Rótulo Máximo</label>
                                <input 
                                    value={selectedField.linearScaleConfig?.maxLabel || ''}
                                    placeholder="ex: Excelente"
                                    onChange={(e) => updateNestedConfig(selectedField.id, 'linearScaleConfig', 'maxLabel', e.target.value)}
                                    className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900"
                                />
                             </div>
                        </div>
                    )}

                    {selectedField.type === FieldType.SIGNATURE && (
                        <div className="mt-6 border-t border-slate-200 pt-4 space-y-4">
                            <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                                <PenTool size={14}/> Regras de Assinatura
                            </h4>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-700">Método de Validação</label>
                                <select 
                                    className="w-full bg-white border border-slate-300 rounded p-2 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500"
                                    value={selectedField.signatureConfig?.method}
                                    onChange={(e) => updateNestedConfig(selectedField.id, 'signatureConfig', 'method', e.target.value)}
                                >
                                    <option value={SignatureMethod.HANDWRITTEN}>Manuscrita (Caneta/Dedo)</option>
                                    <option value={SignatureMethod.BIOMETRIC_VISUAL}>Biométrica (Selfie)</option>
                                    <option value={SignatureMethod.CREDENTIAL}>Credencial (Senha)</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <input 
                                    type="checkbox" 
                                    checked={selectedField.signatureConfig?.allowRefusal}
                                    onChange={(e) => updateNestedConfig(selectedField.id, 'signatureConfig', 'allowRefusal', e.target.checked)}
                                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-slate-300"
                                />
                                <label className="text-sm text-slate-700 font-medium">Permitir Recusa (com Motivo)</label>
                            </div>
                        </div>
                    )}

                    {selectedField.type === FieldType.MEDIA_PHOTO && (
                         <div className="mt-6 border-t border-slate-200 pt-4 space-y-4">
                            <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                                <Camera size={14}/> Configuração de Mídia
                            </h4>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="checkbox" 
                                        id="multi-check"
                                        checked={selectedField.mediaConfig?.allowMultiple}
                                        onChange={(e) => updateNestedConfig(selectedField.id, 'mediaConfig', 'allowMultiple', e.target.checked)}
                                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-slate-300"
                                    />
                                    <label htmlFor="multi-check" className="text-sm text-slate-700 font-medium">Permitir Múltiplos Arquivos</label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="checkbox" 
                                        id="video-check"
                                        checked={selectedField.mediaConfig?.allowVideo}
                                        onChange={(e) => updateNestedConfig(selectedField.id, 'mediaConfig', 'allowVideo', e.target.checked)}
                                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-slate-300"
                                    />
                                    <label htmlFor="video-check" className="text-sm text-slate-700 font-medium">Permitir Vídeo (Beta)</label>
                                </div>
                            </div>
                        </div>
                    )}

                    {[FieldType.SELECT, FieldType.RADIO, FieldType.CHECKBOX].includes(selectedField.type) && (
                        <div className="mt-6 border-t border-slate-200 pt-4 space-y-4">
                            <h4 className="font-bold text-sm text-slate-900">Opções</h4>
                            <div className="space-y-2">
                                {selectedField.options?.map((opt, i) => (
                                    <div key={i} className="flex gap-2">
                                        <input 
                                            className="flex-1 bg-white border border-slate-300 rounded p-1 text-sm text-slate-900 shadow-sm"
                                            value={opt.label}
                                            onChange={(e) => {
                                                const newOpts = [...(selectedField.options || [])];
                                                newOpts[i] = { ...newOpts[i], label: e.target.value, value: e.target.value };
                                                updateField(selectedField.id, 'options', newOpts);
                                            }}
                                        />
                                    </div>
                                ))}
                                <button 
                                    onClick={() => {
                                        const newOpts = [...(selectedField.options || []), { label: 'Nova Opção', value: 'new' }];
                                        updateField(selectedField.id, 'options', newOpts);
                                    }}
                                    className="text-xs text-indigo-700 hover:underline font-bold"
                                >
                                    + Adicionar Opção
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
      </div>

    </div>
  );
};

export default FormBuilder;
